'use client';

/**
 * FaqAccordion — shared V3 page-local FAQ section.
 *
 * Used by every feature page (loyalty-programs, e-signature, payments,
 * business-cards, auto-pilot) and any other V3 page that needs a tight
 * 4–6 question accordion. Renders the canonical pattern: numbered kicker,
 * headline, .sum-faq-* accordion items, and a "See full FAQ" link to
 * /<lang>/faq.
 *
 * Each consumer page builds the question list inline with its own copy
 * (and i18n fallback). The component just renders.
 */

import Link from 'next/link';
import React, { useState } from 'react';
import SectionLabel from '../../home/SectionLabel';
import { Locale } from '../../../../../i18n-config';
import { useTranslation } from '@/i18n/client';

export type FaqItem = {
  /** Stable React key. */
  key: string;
  q: string;
  a: string;
};

type Props = {
  lang: Locale;
  num: string;
  label: string;
  headline: string;
  items: FaqItem[];
  /** "See the full FAQ" link label; defaults to `Layout/A11y/see_full_faq`. */
  fullFaqLabel?: string;
  /** Section background; defaults to `#0A0E27`. */
  background?: string;
  /** Opens /faq on this category (`?category=<id>`). */
  fullFaqCategory?: string;
};

const FaqAccordion = ({
  lang,
  num,
  label,
  headline,
  items,
  fullFaqLabel,
  background = '#0A0E27',
  fullFaqCategory,
}: Props) => {
  const [open, setOpen] = useState<number>(-1);
  const { t } = useTranslation(lang);
  const linkLabel = fullFaqLabel ?? t('Layout/A11y/see_full_faq');

  return (
    <section
      className="px-6 py-24 lg:py-28"
      style={{
        background,
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={num} label={label} />
        <h2
          className="font-bold"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 42px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 48px',
          }}
        >
          {headline}
        </h2>

        <div className="sum-faq-list" style={{ maxWidth: 820 }}>
          {items.map((item, i) => (
            <div key={item.key} className={`sum-faq-item ${open === i ? 'open' : ''}`}>
              <button
                type="button"
                className="sum-faq-q"
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
              >
                <span>{item.q}</span>
                <span className="sum-faq-icon" aria-hidden="true">
                  {open === i ? '−' : '+'}
                </span>
              </button>
              <div className="sum-faq-a">{item.a}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 32 }}>
          <Link
            href={fullFaqCategory ? `/${lang}/faq?category=${fullFaqCategory}` : `/${lang}/faq`}
            className="mono"
            style={{
              fontSize: 13,
              color: '#22D3EE',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              borderBottom: '1px dashed rgba(34,211,238,0.4)',
              paddingBottom: 2,
            }}
          >
            {linkLabel} →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FaqAccordion;
